import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../../../lib/cn';
import { Field, fieldDescribedBy } from './Field';
import { useField } from './useField';
import { controlBoxClass } from './controlStyles';
import type { ControlSize } from './controlStyles';

/* =============================================================================
   InputTextArea
   -----------------------------------------------------------------------------
   The multi-line control. It is a real `<textarea>` — the box itself, not a div
   wearing one — so `controlBoxClass` gives it the shared skin, the focus ring
   and the error/success/disabled ladder, and `Field` renders the label,
   description, message row and the 44px hit-target overlay around it.

   Two things are specific to multi-line:

     - Height. The box skin pins a single-line height, so the textarea overrides
       it with `min-h-*` and takes `py-2` for its own vertical padding. It never
       uses a fixed `h-*`: the box has to be able to grow with its content.
     - The character count. The limit is enforced ONLY by the native `maxLength`
       attribute. The controlled value is never sliced in JS — slicing breaks
       paste and IME/CJK composition — so the count cannot exceed the limit and
       "at the limit" IS the overflow state.
   ============================================================================= */

export interface InputTextAreaProps
  extends Omit<ComponentPropsWithRef<'textarea'>, 'onChange' | 'children' | 'defaultValue'> {
  id: string;
  label?: ReactNode;
  description?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  /** Extra rules on top of `required` — a minimum length, a pattern, a cross-field rule. */
  validate?: (value: string) => string | null;
  /** Enforced by the native attribute; also renders the `N / maxLength` count. */
  maxLength?: number;
  rows?: number;
  /** Submit-level or server override. Wins over the field's own validation. */
  error?: string | null;
  success?: boolean;
  successMessage?: ReactNode;
  size?: ControlSize;
  disabled?: boolean;
  readOnly?: boolean;
  /** Raised while the value is being saved — the box locks and announces busy. */
  loading?: boolean;
  /** Bumped by the form on each submit attempt; reveals the error. */
  submitCount?: number;
  /** Opts back into a vertical drag handle. Off by default — the box scrolls. */
  resize?: boolean;
}

export function InputTextArea({
  id,
  label,
  description,
  value,
  onChange,
  required = false,
  validate,
  maxLength,
  rows = 4,
  error,
  success = false,
  successMessage,
  size = 'md',
  disabled = false,
  readOnly = false,
  loading = false,
  submitCount = 0,
  resize = false,
  onBlur,
  className,
  ...rest
}: InputTextAreaProps) {
  // A textarea has no type rules of its own, so `validate` is whatever the
  // consumer declares. `useField` owns the reveal timing either way.
  const field = useField({ value, onChange, validate, required, submitCount, success });

  const isDisabled = disabled || loading;
  const message = error ?? field.error;
  const invalid = Boolean(message);
  const savedMessage = field.success ? (successMessage ?? 'Saved!') : undefined;
  // The count is part of what the control is described by, so the limit is
  // reachable on focus rather than only audible as it changes.
  const countId = maxLength === undefined ? undefined : `${id}-count`;
  const describedBy =
    [
      fieldDescribedBy(id, {
        description: Boolean(description),
        message: Boolean(message ?? savedMessage),
      }),
      countId,
    ]
      .filter((entry): entry is string => entry !== undefined)
      .join(' ') || undefined;

  // `>=` and not `>`: with the native `maxLength` in place the count can never
  // pass the limit, so the at-the-limit state is the warning state.
  const atLimit = maxLength !== undefined && value.length >= maxLength;

  return (
    <Field
      id={id}
      label={label}
      description={description}
      required={required}
      disabled={isDisabled}
      invalid={invalid}
      error={message}
      successMessage={savedMessage}
      size={size}
      messageAddon={
        maxLength ? (
          // A live region, because the count is the only feedback that the box
          // is about to stop accepting characters.
          <span id={countId} aria-live="polite" className={cn(atLimit && 'text-warning')}>
            {value.length} / {maxLength}
          </span>
        ) : undefined
      }
    >
      <textarea
        {...rest}
        id={id}
        value={value}
        onChange={(event) => field.setValue(event.target.value)}
        onBlur={(event) => {
          // `...rest` is spread first, so the consumer's own `onBlur` would
          // otherwise be silently replaced by this one.
          onBlur?.(event);
          field.onBlur();
        }}
        rows={rows}
        maxLength={maxLength}
        disabled={isDisabled}
        readOnly={readOnly}
        aria-busy={loading || undefined}
        // The form is `noValidate` and this library's error UI owns validation,
        // so the native `required` attribute stays off and `aria-required` speaks.
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className={cn(
          controlBoxClass({
            size,
            invalid,
            success: Boolean(savedMessage),
            disabled: isDisabled,
            readOnly,
          }),
          // `min-h-*` overrides the single-line height, `max-h-64` + `overflow-y-auto`
          // make it scroll instead of growing without bound.
          'min-h-16 max-h-64 overflow-y-auto py-2',
          // The error border outranks the warning one: a field that is both at
          // the limit and invalid must still read as invalid.
          atLimit && !invalid && 'border-warning',
          resize ? 'resize-y' : 'resize-none',
          className,
        )}
      />
    </Field>
  );
}
